import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";
import MapaEscolhaLocalizacao from "../components/ui/MapaEscolhaLocalizacao";
import { capturarMelhorLocalizacao, buscarEndereco, buscarEnderecoPorCep } from "../utils/geolocalizacao";

function formatarCep(v) {
  const digitos = v.replace(/\D/g, "").slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
}

// Três tipos de posto. GPS por coordenada/raio é pouco confiável em
// computador de mesa (sem chip de GPS, o navegador estima por WiFi/IP e erra
// por quilômetros) — por isso é um tipo separado do IP, não misturado: cada
// um é a opção certa pra um cenário físico diferente (escritório com internet
// fixa vs. posto de trabalho batido pelo celular, tipo obra/portaria/loja).
const TIPOS = [
  { valor: "gps", label: "Posto com GPS", icon: "mapPin", desc: "Restringe o registro de ponto por coordenada geográfica e raio de tolerância. Recomendado para dispositivos móveis equipados com GPS." },
  { valor: "flexivel", label: "Sem local fixo", icon: "heart", desc: "Permite o registro de ponto em qualquer localização, mantendo o registro do local de origem." },
  { valor: "escritorio", label: "Escritório", icon: "globe", desc: "Restringe o registro de ponto ao endereço IP fixo da conexão do local." },
];

const VAZIO = { nome: "", endereco: "", tipo: "gps", ip_permitido: "", latitude: "", longitude: "", raio_metros: 100 };

// Mesma regra usada no badge da tabela — deriva o tipo a partir dos campos
// reais do posto (não existe uma coluna `tipo` persistida separada).
function tipoDoPosto(p) {
  if (p.latitude != null) return "gps";
  if (p.exige_localizacao) return "escritorio";
  return "flexivel";
}

export default function Postos() {
  const { token } = useAuth();
  const [postos, setPostos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [buscandoIp, setBuscandoIp] = useState(false);
  const [capturandoLocalizacao, setCapturandoLocalizacao] = useState(false);
  const [precisaoCaptura, setPrecisaoCaptura] = useState(null);
  const [mostrarIpRespaldo, setMostrarIpRespaldo] = useState(false);
  const [buscaEndereco, setBuscaEndereco] = useState("");
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [buscaCep, setBuscaCep] = useState("");
  const [buscaCepNumero, setBuscaCepNumero] = useState("");
  const [buscandoCep, setBuscandoCep] = useState(false);

  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEndereco, setFiltroEndereco] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/postos", undefined, token);
      setPostos(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setEditando(null);
    setForm(VAZIO);
    setPrecisaoCaptura(null);
    setMostrarIpRespaldo(false);
    setBuscaEndereco("");
    setBuscaCep("");
    setBuscaCepNumero("");
    setErro("");
    setModalOpen(true);
  };

  const abrirEdicao = (posto) => {
    setEditando(posto);
    const tipo = posto.latitude != null ? "gps" : posto.exige_localizacao ? "escritorio" : "flexivel";
    setForm({
      nome: posto.nome || "",
      endereco: posto.endereco || "",
      tipo,
      ip_permitido: posto.ip_permitido || "",
      latitude: posto.latitude != null ? String(posto.latitude) : "",
      longitude: posto.longitude != null ? String(posto.longitude) : "",
      raio_metros: posto.raio_metros || 100,
    });
    setPrecisaoCaptura(null);
    setMostrarIpRespaldo(tipo === "gps" && !!posto.ip_permitido);
    setBuscaEndereco("");
    setBuscaCep("");
    setBuscaCepNumero("");
    setErro("");
    setModalOpen(true);
  };

  // Coordenada escolhida no mapa (clique/arraste) ou por busca de endereço —
  // não é uma leitura de GPS de verdade, então não tem "precisão" pra mostrar
  // (diferente do botão de localização atual, que sim).
  const onMapaChange = (lat, lon) => {
    setForm((f) => ({ ...f, latitude: String(lat), longitude: String(lon) }));
    setPrecisaoCaptura(null);
  };

  const buscarEnderecoNoMapa = async () => {
    if (!buscaEndereco.trim()) return;
    setErro("");
    setBuscandoEndereco(true);
    try {
      const resultado = await buscarEndereco(buscaEndereco.trim());
      if (!resultado) {
        setErro("Endereço não encontrado — tente ser mais específico (rua, número, cidade) ou ajuste direto no mapa.");
        return;
      }
      onMapaChange(resultado.latitude, resultado.longitude);
      setForm((f) => ({ ...f, endereco: resultado.nomeEncontrado || buscaEndereco.trim() }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscandoEndereco(false);
    }
  };

  const buscarPorCep = async () => {
    if (!buscaCep.trim()) return;
    setErro("");
    setBuscandoCep(true);
    try {
      const resultado = await buscarEnderecoPorCep(buscaCep, buscaCepNumero.trim());
      onMapaChange(resultado.latitude, resultado.longitude);
      setForm((f) => ({ ...f, endereco: resultado.enderecoCompleto }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscandoCep(false);
    }
  };

  const usarMinhaLocalizacaoAtual = async () => {
    setErro("");
    if (!navigator.geolocation) {
      setErro("Esse navegador não suporta captura de localização.");
      return;
    }
    setCapturandoLocalizacao(true);
    // Configuração é uma ação única e deliberada (a pessoa já está parada no
    // local de propósito) — vale esperar mais tempo por uma leitura ótima do
    // que na hora de bater ponto no dia a dia.
    const local = await capturarMelhorLocalizacao({ duracaoMs: 12000, precisaoAlvo: 15 });
    setCapturandoLocalizacao(false);
    if (!local) {
      setErro("Não foi possível capturar sua localização — verifique se o navegador tem permissão de GPS.");
      return;
    }
    setForm((f) => ({ ...f, latitude: String(local.latitude), longitude: String(local.longitude) }));
    setPrecisaoCaptura(Math.round(local.accuracy));
  };

  const usarMeuIpAtual = async () => {
    setErro("");
    setBuscandoIp(true);
    try {
      const { ip } = await apiFetch("GET", "/api/postos/meu-ip", undefined, token);
      setForm((f) => ({ ...f, ip_permitido: f.ip_permitido ? `${f.ip_permitido}, ${ip}` : ip }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscandoIp(false);
    }
  };

  const salvar = async () => {
    setErro("");
    if (!form.nome) {
      setErro("Informe o nome do posto");
      return;
    }
    if (form.tipo === "gps" && (!form.latitude || !form.longitude)) {
      setErro('Capture a localização (botão "Usar minha localização atual") antes de salvar.');
      return;
    }
    setSalvando(true);
    try {
      const ehEscritorio = form.tipo === "escritorio";
      const ehGps = form.tipo === "gps";
      const payload = {
        nome: form.nome,
        endereco: form.endereco,
        exige_localizacao: ehEscritorio || ehGps,
        ip_permitido: ehEscritorio || ehGps ? form.ip_permitido : "",
        latitude: ehGps ? form.latitude : "",
        longitude: ehGps ? form.longitude : "",
        raio_metros: ehGps ? Number(form.raio_metros) || 100 : 100,
      };
      if (editando) {
        await apiFetch("PUT", `/api/postos/${editando.id}`, payload, token);
      } else {
        await apiFetch("POST", "/api/postos", payload, token);
      }
      setModalOpen(false);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (posto) => {
    try {
      await apiFetch("PUT", `/api/postos/${posto.id}`, { ativo: !posto.ativo }, token);
      carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const excluir = async (posto) => {
    if (!window.confirm(`Excluir o posto "${posto.nome}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await apiFetch("DELETE", `/api/postos/${posto.id}`, undefined, token);
      carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const postosFiltrados = postos.filter((p) => {
    if (filtroNome && !p.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroEndereco && !(p.endereco || "").toLowerCase().includes(filtroEndereco.toLowerCase())) return false;
    if (filtroTipo && tipoDoPosto(p) !== filtroTipo) return false;
    if (filtroStatus && (filtroStatus === "ativo") !== !!p.ativo) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroNome || filtroEndereco || filtroTipo || filtroStatus);
  const limparFiltros = () => { setFiltroNome(""); setFiltroEndereco(""); setFiltroTipo(""); setFiltroStatus(""); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Postos
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Cadastre locais de trabalho onde seus funcionários vão bater o ponto.
          </p>
        </div>
        <Btn onClick={abrirNovo}>+ Novo posto</Btn>
      </div>

      {postos.length > 0 && (
        <BarraResumoFiltro
          total={postosFiltrados.length}
          totalGeral={postos.length}
          unidade="posto(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : postos.length === 0 ? (
          <EstadoVazio icon="mapPin" texto="Nenhum posto cadastrado ainda." />
        ) : postosFiltrados.length === 0 ? (
          <EstadoVazio icon="mapPin" texto="Nenhum posto encontrado com esse filtro." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <ThFiltroTexto label="Posto" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={150} />
                <ThFiltroTexto label="Endereço" value={filtroEndereco} onChange={setFiltroEndereco} placeholder="Buscar..." minWidth={170} />
                <ThFiltroSelect
                  label="Tipo" value={filtroTipo} onChange={setFiltroTipo} minWidth={150}
                  opcoes={[{ value: "", label: "Todos" }, ...TIPOS.map((t) => ({ value: t.valor, label: t.label }))]}
                />
                <ThSimples label="Funcionários" />
                <ThFiltroSelect
                  label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={110}
                  opcoes={[{ value: "", label: "Todos" }, { value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]}
                />
                <ThSimples label="" />
              </tr>
            </thead>
            <tbody>
              {postosFiltrados.map((p) => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "14px 20px", fontSize: 13.5 }}>
                    <button onClick={() => abrirEdicao(p)} style={{ background: "transparent", border: "none", padding: 0, fontWeight: 700, color: COLORS.text, cursor: "pointer", textAlign: "left" }}>
                      {p.nome}
                    </button>
                  </td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{p.endereco || "—"}</td>
                  <td style={{ padding: "14px 20px" }}>
                    <Badge tone={tipoDoPosto(p) === "flexivel" ? "neutral" : "primary"}>
                      {TIPOS.find((t) => t.valor === tipoDoPosto(p))?.label}
                    </Badge>
                  </td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{p.total_funcionarios}</td>
                  <td style={{ padding: "14px 20px" }}>
                    <Badge tone={p.ativo ? "success" : "neutral"}>{p.ativo ? "Ativo" : "Inativo"}</Badge>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => abrirEdicao(p)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer", marginRight: 8 }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => alternarAtivo(p)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer", marginRight: 8 }}
                    >
                      {p.ativo ? "Desativar" : "Ativar"}
                    </button>
                    <button
                      onClick={() => excluir(p)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.danger, cursor: "pointer" }}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editando ? "Editar posto" : "Novo posto"} width={form.tipo === "gps" ? 560 : 460}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nome do posto" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Escritório Central, Home Office" />

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 8 }}>Tipo de posto</label>
            {/* GPS é a opção recomendada — ocupa a linha inteira sozinho, em
                destaque; as outras duas dividem a linha de baixo. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <TipoCard tipo={TIPOS[0]} ativo={form.tipo === TIPOS[0].valor} onClick={() => setForm({ ...form, tipo: TIPOS[0].valor })} />
              <div style={{ display: "flex", gap: 8 }}>
                {TIPOS.slice(1).map((t) => (
                  <TipoCard key={t.valor} tipo={t} ativo={form.tipo === t.valor} onClick={() => setForm({ ...form, tipo: t.valor })} />
                ))}
              </div>
            </div>
          </div>

          {form.tipo === "escritorio" ? (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
              <Input
                label="IP autorizado"
                value={form.ip_permitido}
                onChange={(e) => setForm({ ...form, ip_permitido: e.target.value })}
                placeholder="Ex: 187.45.12.90"
              />
              <Btn variant="secondary" onClick={usarMeuIpAtual} disabled={buscandoIp} style={{ width: "100%", marginTop: 8 }}>
                {buscandoIp ? "Buscando IP..." : "🌐 Usar meu IP atual"}
              </Btn>
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "8px 0 0" }}>
                Clique nesse botão estando no computador do escritório, pra capturar o IP de lá — é o único jeito confiável, um site externo de "qual é meu IP" pode informar um endereço diferente do que o sistema realmente vê. Pode adicionar mais de um IP, separado por vírgula.
              </p>
              {!form.ip_permitido && (
                <p style={{ fontSize: 11.5, color: COLORS.warning, margin: "8px 0 0", fontWeight: 600 }}>
                  Sem nenhum IP cadastrado ainda, esse posto não bloqueia nada — funciona como "sem local fixo" até você configurar.
                </p>
              )}
            </div>
          ) : form.tipo === "gps" ? (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ width: 130 }}>
                  <Input
                    label="Buscar por CEP"
                    value={buscaCep}
                    onChange={(e) => setBuscaCep(formatarCep(e.target.value))}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), buscarPorCep())}
                    placeholder="00000-000"
                  />
                </div>
                <div style={{ width: 80 }}>
                  <Input
                    label="Número"
                    value={buscaCepNumero}
                    onChange={(e) => setBuscaCepNumero(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), buscarPorCep())}
                    placeholder="Nº"
                  />
                </div>
                <Btn variant="secondary" onClick={buscarPorCep} disabled={buscandoCep} style={{ marginBottom: 1 }}>
                  {buscandoCep ? "Buscando..." : "Buscar CEP"}
                </Btn>
              </div>
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                Jeito mais preciso de achar o local — o CEP resolve a rua e o bairro certos direto dos Correios, sem depender de digitar o endereço igualzinho. Coloque o número da casa/prédio se quiser ainda mais precisão.
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0" }}>
                <div style={{ flex: 1, height: 1, background: COLORS.border }} />
                <span style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 600 }}>OU</span>
                <div style={{ flex: 1, height: 1, background: COLORS.border }} />
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Buscar por nome da rua"
                    value={buscaEndereco}
                    onChange={(e) => setBuscaEndereco(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), buscarEnderecoNoMapa())}
                    placeholder="Rua, número, bairro, cidade"
                  />
                </div>
                <Btn variant="secondary" onClick={buscarEnderecoNoMapa} disabled={buscandoEndereco} style={{ marginBottom: 1 }}>
                  {buscandoEndereco ? "Buscando..." : "Buscar"}
                </Btn>
              </div>

              <div style={{ marginTop: 10 }}>
                <MapaEscolhaLocalizacao
                  latitude={form.latitude ? Number(form.latitude) : null}
                  longitude={form.longitude ? Number(form.longitude) : null}
                  onChange={onMapaChange}
                />
              </div>

              <Btn variant="secondary" onClick={usarMinhaLocalizacaoAtual} disabled={capturandoLocalizacao} style={{ width: "100%", marginTop: 10 }}>
                {capturandoLocalizacao ? "Capturando... (pode levar alguns segundos)" : "📍 Usar minha localização atual"}
              </Btn>
              <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "8px 0 0" }}>
                Formas de definir o ponto: buscar por CEP (mais preciso), buscar pelo nome da rua, clicar/arrastar no mapa, ou usar esse botão estando fisicamente no local (só funciona bem no celular — computador de mesa sem GPS estima por WiFi/IP e pode errar por quilômetros).
              </p>

              {form.latitude && form.longitude && (
                <div style={{ marginTop: 10, padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md }}>
                  <p style={{ fontSize: 12.5, color: COLORS.text, margin: 0, fontVariantNumeric: "tabular-nums" }}>
                    {Number(form.latitude).toFixed(6)}, {Number(form.longitude).toFixed(6)}
                  </p>
                  {precisaoCaptura != null && (
                    <p style={{ fontSize: 11.5, margin: "4px 0 0", color: precisaoCaptura > 200 ? COLORS.warning : COLORS.textLight, fontWeight: precisaoCaptura > 200 ? 700 : 400 }}>
                      Precisão: ~{precisaoCaptura}m
                      {precisaoCaptura > 200 ? " — parece impreciso pra um computador sem GPS. Tente pelo celular, com o GPS ativado." : ""}
                    </p>
                  )}
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <Input
                  label="Raio de tolerância (metros)"
                  type="number"
                  min="10"
                  value={form.raio_metros}
                  onChange={(e) => setForm({ ...form, raio_metros: e.target.value })}
                />
                <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 0" }}>
                  Distância máxima do ponto capturado pra ainda contar como "no posto". Sugestão: ~100m se o pessoal bate ponto pelo celular; ~300-500m se for de computador de escritório (GPS de computador é menos preciso).
                </p>
              </div>

              <div style={{ marginTop: 14, borderTop: `1px solid ${COLORS.border}`, paddingTop: 12 }}>
                {mostrarIpRespaldo ? (
                  <>
                    <Input
                      label="IP de respaldo (opcional)"
                      value={form.ip_permitido}
                      onChange={(e) => setForm({ ...form, ip_permitido: e.target.value })}
                      placeholder="Ex: 187.45.12.90"
                    />
                    <Btn variant="secondary" onClick={usarMeuIpAtual} disabled={buscandoIp} style={{ width: "100%", marginTop: 8 }}>
                      {buscandoIp ? "Buscando IP..." : "🌐 Usar meu IP atual"}
                    </Btn>
                    <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "8px 0 0" }}>
                      Rede de segurança pra quando o GPS de um computador específico vier impreciso demais: se o IP bater, libera mesmo assim. Continua contando como "Posto com GPS" na lista.
                    </p>
                  </>
                ) : (
                  <button
                    onClick={() => setMostrarIpRespaldo(true)}
                    style={{ background: "none", border: "none", color: COLORS.primary, fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}
                  >
                    + Adicionar IP de respaldo (opcional)
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 12, color: COLORS.textMid, margin: 0, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "10px 12px", lineHeight: 1.5 }}>
              Funcionários vinculados a esse posto podem bater ponto de qualquer lugar, sem bloqueio. A localização, quando o navegador conseguir capturar, continua sendo salva em cada batida, só pra você acompanhar de onde foi.
            </p>
          )}

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}

          <Btn onClick={salvar} disabled={salvando} style={{ width: "100%", marginTop: 4 }}>
            {salvando ? "Salvando..." : editando ? "Salvar alterações" : "Criar posto"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

function TipoCard({ tipo, ativo, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6,
        padding: "12px 14px", borderRadius: RADIUS.md, cursor: "pointer", textAlign: "left",
        border: `1.5px solid ${ativo ? COLORS.primary : COLORS.border}`,
        background: ativo ? COLORS.primaryBg : COLORS.white,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: ativo ? COLORS.primary : COLORS.text }}>
        <Icon name={tipo.icon} size={15} />
        {tipo.label}
      </span>
      <span style={{ fontSize: 11, color: COLORS.textMid, lineHeight: 1.4 }}>{tipo.desc}</span>
    </button>
  );
}
